import {useAuth} from '@common/auth/use-auth';
import {useTrans} from '@ui/i18n/use-trans';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {useSettings} from '@ui/settings/use-settings';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {cn} from '@ui/utils/cn';
import {ReactNode} from 'react';
import {Link} from 'react-router';

type BrandingLogos = {
  logo_dark?: string;
  logo_light?: string;
  logo_dark_mobile?: string;
  logo_light_mobile?: string;
  logo_dark_rtl?: string;
  logo_light_rtl?: string;
  logo_dark_mobile_rtl?: string;
  logo_light_mobile_rtl?: string;
};

type LogoTone = 'dark' | 'light';

function useIsRtlUi(): boolean {
  return useBootstrapDataStore(s => s.data.i18n?.direction) === 'rtl';
}

/** Prefer RTL-specific logo when UI is RTL; otherwise fall back to the default LTR logo. */
function resolveLogoSrc(
  branding: BrandingLogos,
  color: LogoTone,
  compact: boolean,
  isRtl: boolean,
): string | undefined {
  if (isRtl) {
    if (compact) {
      const rtlCompact =
        color === 'dark'
          ? branding.logo_dark_mobile_rtl || branding.logo_dark_rtl
          : branding.logo_light_mobile_rtl || branding.logo_light_rtl;
      if (rtlCompact) return rtlCompact;
    } else {
      const rtlWide =
        color === 'dark' ? branding.logo_dark_rtl : branding.logo_light_rtl;
      if (rtlWide) return rtlWide;
    }
  }

  if (compact) {
    if (color === 'dark' && branding.logo_dark_mobile) {
      return branding.logo_dark_mobile;
    }
    return branding.logo_light_mobile || undefined;
  }

  if (color === 'dark' && branding.logo_dark) {
    return branding.logo_dark;
  }
  return branding.logo_light || undefined;
}

type Props = {
  color?: 'dark' | 'light' | 'auto';
  className?: string;
  logoType?: 'wide' | 'compact' | 'auto';
  url?: string;
};
export function Logo({
  color = 'auto',
  className,
  logoType = 'auto',
  url,
}: Props) {
  const isDarkMode = useIsDarkMode();

  if (color === 'auto') {
    color = isDarkMode ? 'light' : 'dark';
  }

  if (logoType === 'compact') {
    return <CompactLogo color={color} className={className} url={url} />;
  } else if (logoType === 'wide') {
    return <WideLogo color={color} className={className} url={url} />;
  }

  return <AutoLogo color={color} className={className} url={url} />;
}

function CompactLogo({color, className, url}: Omit<Props, 'logoType'>) {
  const {branding} = useSettings();
  const isRtl = useIsRtlUi();
  const src = resolveLogoSrc(branding, color as LogoTone, true, isRtl);

  if (!src) return null;

  return (
    <WrapperLink url={url} className={cn('block h-full max-w-full min-w-0 overflow-hidden', className)}>
      <img
        src={src}
        className="block h-full w-auto max-w-full object-contain object-start"
        alt=""
      />
    </WrapperLink>
  );
}

function AutoLogo({color, className, url}: Omit<Props, 'logoType'>) {
  const {branding} = useSettings();
  const isRtl = useIsRtlUi();

  const wideLogo = resolveLogoSrc(branding, color as LogoTone, false, isRtl);
  const compactLogo = resolveLogoSrc(branding, color as LogoTone, true, isRtl);

  if (!wideLogo && !compactLogo) {
    return null;
  }

  return (
    <WrapperLink url={url} className={cn('block h-full max-w-full min-w-0 overflow-hidden', className)}>
      <picture className="block h-full max-w-full overflow-hidden">
        <source srcSet={compactLogo || wideLogo} media="(max-width: 768px)" />
        <source srcSet={wideLogo || compactLogo} media="(min-width: 768px)" />
        <img
          className="block h-full w-auto max-w-full object-contain object-start"
          alt=""
        />
      </picture>
    </WrapperLink>
  );
}

function WideLogo({color, className, url}: Omit<Props, 'logoType'>) {
  const {branding} = useSettings();
  const isRtl = useIsRtlUi();
  const src = resolveLogoSrc(branding, color as LogoTone, false, isRtl);

  if (!src) return null;

  return (
    <WrapperLink url={url} className={cn('block h-full max-w-full min-w-0 overflow-hidden', className)}>
      <img
        src={src}
        className="block h-full w-auto max-w-full object-contain object-start"
        alt=""
      />
    </WrapperLink>
  );
}

type WrapperLinkProps = {
  children: ReactNode;
  url?: string;
  className?: string;
};
function WrapperLink({children, url, className}: WrapperLinkProps) {
  const {trans} = useTrans();
  const {getRedirectUri} = useAuth();

  return (
    <Link
      to={url || getRedirectUri()}
      className={cn('block h-full max-w-full min-w-0 shrink-0 overflow-hidden', className)}
      aria-label={trans({message: 'Go to homepage'})}
    >
      {children}
    </Link>
  );
}
